/**
 * Watchlist page.
 *
 * Tracks symbols you're researching, each with its latest local price and
 * day change, plus a "Buy (paper)" action: log a simulated position at
 * today's price with no real money involved, then watch whether it rose or
 * fell. Prices shown here come from the local store — sync a ticker (Market
 * page or Research) first if a row shows no price yet.
 */

import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "@/api/client";
import type { PaperPosition, WatchlistItem } from "@/types";
import SyncTickerPanel from "@/components/research/SyncTickerPanel";

const fmtPrice = (v: number | null): string =>
  v === null || v === undefined ? "—" : `$${v.toFixed(2)}`;
const fmtPct = (v: number | null): string =>
  v === null || v === undefined ? "—" : `${v >= 0 ? "+" : ""}${v.toFixed(2)}%`;
const toneClass = (v: number | null): string => {
  if (v === null || v === undefined) return "text-mist-400";
  return v > 0 ? "text-emerald-400" : v < 0 ? "text-rose-400" : "text-mist-300";
};

function BuyModal({
  symbol,
  onClose,
  onBought,
}: {
  symbol: string;
  onClose: () => void;
  onBought: () => void;
}): React.ReactElement {
  const [dollarAmount, setDollarAmount] = useState("1000");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    const amount = Number(dollarAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setError("Enter a dollar amount greater than 0.");
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      await apiClient.post("/paper-trades", { symbol, dollar_amount: amount });
      onBought();
      onClose();
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detail ? String(detail) : "Couldn't log that paper trade.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-sm rounded-xl border border-ink-700 bg-ink-900 p-5">
        <h3 className="text-lg font-semibold text-mist-100">Buy {symbol} (paper)</h3>
        <p className="mt-1 text-sm text-mist-400">
          Simulated only — no real money moves. Logs a position at today's local price so you can
          track it from here.
        </p>
        <label className="mt-4 block">
          <span className="text-xs text-mist-400">Amount to "spend"</span>
          <div className="mt-1 flex items-center gap-1">
            <span className="text-mist-500">$</span>
            <input
              type="number"
              min={0}
              value={dollarAmount}
              onChange={(e) => setDollarAmount(e.target.value)}
              className="w-full rounded-lg border border-ink-600 bg-ink-950 px-3 py-2 text-sm text-mist-100 focus:border-ink-500 focus:outline-none"
              autoFocus
            />
          </div>
        </label>
        {error && <p className="mt-2 text-sm text-rose-400">{error}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-ink-700 px-4 py-2 text-sm text-mist-300 hover:border-ink-500"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={isSubmitting}
            className="rounded-lg border border-signal-teal/40 bg-signal-teal/10 px-4 py-2 text-sm font-medium text-signal-teal hover:bg-signal-teal/20 disabled:opacity-60"
          >
            {isSubmitting ? "Buying…" : "Confirm paper buy"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Watchlist(): React.ReactElement {
  const [items, setItems] = useState<WatchlistItem[]>([]);
  const [positions, setPositions] = useState<PaperPosition[]>([]);
  const [openPl, setOpenPl] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newSymbol, setNewSymbol] = useState("");
  const [buyTarget, setBuyTarget] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      const [watchlistRes, tradesRes] = await Promise.all([
        apiClient.get<{ items: WatchlistItem[] }>("/watchlist"),
        apiClient.get<{ positions: PaperPosition[]; open_pl_dollars: number }>("/paper-trades"),
      ]);
      setItems(watchlistRes.data.items ?? []);
      setPositions(tradesRes.data.positions ?? []);
      setOpenPl(tradesRes.data.open_pl_dollars ?? 0);
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detail ? String(detail) : "Couldn't load your watchlist.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const addSymbol = async (): Promise<void> => {
    const symbol = newSymbol.trim().toUpperCase();
    if (!symbol) return;
    try {
      await apiClient.post("/watchlist/items", { symbol });
      setNewSymbol("");
      await load();
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detail ? String(detail) : "Couldn't add that symbol.");
    }
  };

  const removeSymbol = async (itemId: string): Promise<void> => {
    try {
      await apiClient.delete(`/watchlist/items/${itemId}`);
      await load();
    } catch {
      // best-effort; leave the row if the delete fails
    }
  };

  const closePosition = async (positionId: string): Promise<void> => {
    try {
      await apiClient.post(`/paper-trades/${positionId}/close`);
      await load();
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detail ? String(detail) : "Couldn't close that position.");
    }
  };

  const deletePosition = async (positionId: string): Promise<void> => {
    try {
      await apiClient.delete(`/paper-trades/${positionId}`);
      await load();
    } catch {
      // best-effort
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Watchlist</h1>
        <p className="mt-1 text-sm text-mist-400">
          Track symbols you're researching, and try a simulated "paper" buy to see how they play
          out — no real money involved.
        </p>
      </div>

      <div className="panel flex flex-wrap items-center gap-2 p-4">
        <input
          type="text"
          value={newSymbol}
          onChange={(e) => setNewSymbol(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && void addSymbol()}
          placeholder="Add a ticker (e.g. AAPL)"
          className="w-48 rounded-lg border border-ink-600 bg-ink-900 px-3 py-2 text-sm text-mist-100 placeholder:text-mist-500 focus:border-ink-500 focus:outline-none"
        />
        <button
          type="button"
          onClick={() => void addSymbol()}
          className="rounded-lg border border-ink-600 bg-ink-800 px-4 py-2 text-sm font-medium text-mist-100 hover:border-ink-500"
        >
          Add to watchlist
        </button>
      </div>

      <SyncTickerPanel
        title="Fetch a ticker"
        blurb="Pull the latest prices for a symbol from the built-in free sources (SEC EDGAR + Stooq) — no setup or API keys. Data is stored locally so it shows up in your watchlist."
        onSynced={() => void load()}
      />

      {error && <p className="text-sm text-rose-400">{error}</p>}
      {isLoading && <p className="text-sm text-mist-400">Loading…</p>}

      {!isLoading && (
        <div className="panel overflow-x-auto">
          <table className="w-full min-w-[560px] text-left text-sm">
            <thead className="border-b border-ink-700 text-xs uppercase tracking-wide text-mist-400">
              <tr>
                <th className="px-3 py-3">Symbol</th>
                <th className="px-3 py-3">Price</th>
                <th className="px-3 py-3">Change</th>
                <th className="px-3 py-3">Notes</th>
                <th className="px-3 py-3" />
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-8 text-center text-mist-400">
                    No symbols yet. Add one above to start tracking it.
                  </td>
                </tr>
              )}
              {items.map((item) => (
                <tr key={item.id} className="border-b border-ink-800 last:border-0">
                  <td className="px-3 py-3">
                    {item.company_id ? (
                      <Link
                        to={`/research/${item.company_id}`}
                        className="font-medium text-signal-teal hover:underline"
                      >
                        {item.symbol}
                      </Link>
                    ) : (
                      <span className="font-medium text-mist-100">{item.symbol}</span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-mist-100">{fmtPrice(item.price)}</td>
                  <td className={`px-3 py-3 font-medium ${toneClass(item.change_pct)}`}>
                    {fmtPct(item.change_pct)}
                  </td>
                  <td className="px-3 py-3 text-mist-400">{item.notes ?? "—"}</td>
                  <td className="px-3 py-3 text-right">
                    <div className="flex justify-end gap-3">
                      <button
                        type="button"
                        onClick={() => setBuyTarget(item.symbol)}
                        className="text-signal-teal hover:underline"
                      >
                        Buy (paper)
                      </button>
                      <button
                        type="button"
                        onClick={() => void removeSymbol(item.id)}
                        className="text-mist-500 hover:text-rose-400"
                      >
                        Remove
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-lg font-semibold text-mist-100">Paper positions</h2>
          {positions.some((p) => p.is_open) && (
            <span className={`text-sm font-medium ${toneClass(openPl)}`}>
              Open P&amp;L: {openPl >= 0 ? "+" : ""}
              {fmtPrice(Math.abs(openPl)).replace("$", openPl < 0 ? "-$" : "$")}
            </span>
          )}
        </div>
        <p className="mt-1 text-sm text-mist-400">
          Simulated buys — nothing here is real money or a real trade, just tracking to see if a
          call would have worked out.
        </p>

        {positions.length === 0 ? (
          <p className="panel mt-3 p-5 text-sm text-mist-400">
            No paper positions yet. Click "Buy (paper)" next to any symbol above to start one.
          </p>
        ) : (
          <div className="panel mt-3 overflow-x-auto">
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead className="border-b border-ink-700 text-xs uppercase tracking-wide text-mist-400">
                <tr>
                  <th className="px-3 py-3">Symbol</th>
                  <th className="px-3 py-3">Shares</th>
                  <th className="px-3 py-3">Entry</th>
                  <th className="px-3 py-3">Now</th>
                  <th className="px-3 py-3">P&amp;L</th>
                  <th className="px-3 py-3">Status</th>
                  <th className="px-3 py-3" />
                </tr>
              </thead>
              <tbody>
                {positions.map((p) => (
                  <tr key={p.id} className="border-b border-ink-800 last:border-0">
                    <td className="px-3 py-3 font-medium text-mist-100">{p.symbol}</td>
                    <td className="px-3 py-3 text-mist-300">{Number(p.shares).toFixed(4)}</td>
                    <td className="px-3 py-3 text-mist-300">
                      {fmtPrice(p.entry_price)}
                      <span className="ml-1 text-xs text-mist-500">{p.entry_date}</span>
                    </td>
                    <td className="px-3 py-3 text-mist-100">
                      {fmtPrice(p.is_open ? p.current_price : p.exit_price)}
                    </td>
                    <td className={`px-3 py-3 font-medium ${toneClass(p.pl_pct)}`}>
                      {p.pl_dollars !== null ? fmtPrice(p.pl_dollars) : "—"}{" "}
                      <span className="text-xs">({fmtPct(p.pl_pct)})</span>
                    </td>
                    <td className="px-3 py-3">
                      {p.is_open ? (
                        <span className="text-emerald-400">Open</span>
                      ) : (
                        <span className="text-mist-500">Closed {p.exit_date}</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-right">
                      {p.is_open ? (
                        <button
                          type="button"
                          onClick={() => void closePosition(p.id)}
                          className="text-signal-amber hover:underline"
                        >
                          Close (sell)
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => void deletePosition(p.id)}
                          className="text-mist-500 hover:text-rose-400"
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {buyTarget && (
        <BuyModal
          symbol={buyTarget}
          onClose={() => setBuyTarget(null)}
          onBought={() => void load()}
        />
      )}
    </div>
  );
}
